/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { motion, Transition, useAnimation, useInView, Variant } from "framer-motion";
import { forwardRef, useEffect, useRef } from "react";
import { useMergedRefs } from "@components/utils/mergeRefs";
import { css, cx } from "@panda/css";
import { SystemStyleObject } from "@panda/types";
import { useIsRtl } from "@components/hooks";
import { useCosmosComponentContext } from "@components/context/ComponentContext";

export type AnimateProps = {
	children: React.ReactNode;
	delay?: number;
	type?: "fade" | "slide" | "slideLeft" | "slideRight";
	distance?: string;
	customCss?: SystemStyleObject;
};

interface AnimationVariant {
	[key: string]: {
		hidden: Variant;
		visible: Variant;
	};
}

export const Animate = forwardRef(
	(
		{ children, delay = 0, type = "fade", customCss, distance }: AnimateProps,
		forwardedRef: React.Ref<HTMLDivElement>,
	): React.JSX.Element => {
		const { disableAnimations = false } = useCosmosComponentContext();
		const ref = useRef<Element>(null);
		const mergedRef = useMergedRefs(ref, forwardedRef);

		const controls = useAnimation();
		const isInView = useInView(ref as React.RefObject<Element>, {
			amount: 0,
			margin: "0px 0px -32px 0px",
			once: true,
		});
		const isRtl = useIsRtl();

		const transition: Transition = {
			ease: "easeOut",
			opacity: { duration: 0.2, delay },
			y: { duration: 1, delay },
			x: { duration: 1, delay },
		};

		const animationVariants: AnimationVariant = {
			fade: {
				hidden: { opacity: 0.0001, transition },
				visible: { opacity: 1, transition },
			},
			slide: {
				hidden: { opacity: 0.0001, transition, y: distance || "1.75rem" },
				visible: { opacity: 1, transition, y: 0 },
			},
			slideLeft: {
				hidden: { opacity: 0.0001, transition, x: distance || "1rem" },
				visible: { opacity: 1, transition, x: 0 },
			},
			slideRight: {
				hidden: { opacity: 0.0001, transition, x: distance ? `-${distance}` : "-1.5rem" },
				visible: { opacity: 1, transition, x: 0 },
			},
		};

		useEffect(() => {
			if (disableAnimations) return;

			if (isInView) {
				controls.start("visible").catch((e) => {
					throw e;
				});
			}
		}, [controls, isInView, disableAnimations]);

		const finalType = isRtl && type === "slideLeft" ? "slideRight" : type;

		if (disableAnimations) {
			return (
				<div className={cx("animate", css(customCss))} ref={mergedRef}>
					{children}
				</div>
			);
		}

		return (
			<motion.div
				className={cx("animate", css(customCss))}
				animate={controls}
				initial="hidden"
				ref={mergedRef}
				variants={{
					hidden: animationVariants[finalType].hidden,
					visible: animationVariants[finalType].visible,
				}}
			>
				{children}
			</motion.div>
		);
	},
);

Animate.displayName = "Animate";
